import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { OrderItemProofApi } from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { currentWorkspaceAtom } from "@/store";

const orderItemProofApiClient = createApiClient(OrderItemProofApi);

export const useOrderItemProofs = () => {
  const queryClient = useQueryClient();

  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const useApproveOrderItemProof = () =>
    useMutation<
      Awaited<
        ReturnType<
          typeof orderItemProofApiClient.orderItemProofsControllerPatchOrderItemProof
        >
      >,
      Error,
      {
        orderId: string;
        orderItemId: string;
        sandboxItemProofId: string;
        metaData: { orderId: string | undefined };
      }
    >({
      mutationFn: (params) =>
        orderItemProofApiClient.orderItemProofsControllerPatchOrderItemProof({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          sandboxItemProofId: params.sandboxItemProofId,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemProofPatchCommand: {
            isValidated: true,
            isCancelled: false,
          },
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]),
    });

  const useRefuseOrderItemProof = () =>
    useMutation({
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        sandboxItemProofId: string;
        sandboxItemProofRefuseCommand: Parameters<
          typeof orderItemProofApiClient.orderItemProofsControllerPatchOrderItemProof
        >[0]["orderItemProofPatchCommand"];
      }) =>
        orderItemProofApiClient.orderItemProofsControllerPatchOrderItemProof({
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemProofPatchCommand: params.sandboxItemProofRefuseCommand,
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          sandboxItemProofId: params.sandboxItemProofId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]),
    });

  const useCancelOrderItemProof = () =>
    useMutation({
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        sandboxItemProofId: string;
        sandboxItemProofCancelCommand: Parameters<
          typeof orderItemProofApiClient.orderItemProofsControllerPatchOrderItemProof
        >[0]["orderItemProofPatchCommand"];
      }) =>
        orderItemProofApiClient.orderItemProofsControllerPatchOrderItemProof({
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemProofPatchCommand: {
            isCancelled: true,
            cancelReason: params.sandboxItemProofCancelCommand.cancelReason,
            isValidated: true,
          },
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          sandboxItemProofId: params.sandboxItemProofId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]),
    });
  return {
    useApproveOrderItemProof,
    useRefuseOrderItemProof,
    useCancelOrderItemProof,
  };
};
